/**
 * Loads the companion script for the active product card version, if it has one.
 *
 * product-card.js is always loaded as a static <script src> tag per section, so it
 * runs even when the section's product cards are injected later via `innerHTML`
 * (e.g. Shopify's native AJAX product recommendations) — <script> tags inserted
 * through `innerHTML` are inert and never execute. This bootstraps the file below
 * through the DOM API instead, which does execute regardless of how the markup
 * was inserted.
 *
 * `window.theme.productCardVersion` and `window.theme.productCardVersionScripts`
 * are set in layout/theme.liquid from `settings.product_card_version` and
 * `'product-card-v2.js' | asset_url` respectively. The URL must come from Liquid's
 * `asset_url` (not be derived here, e.g. via string-replacing this file's own
 * src) — each asset has its own cache-busting version hash, and reusing this
 * file's hash for a different file's URL means the browser/CDN keeps serving
 * whatever was cached under that URL, ignoring later edits to that file.
 *
 * Add an entry here whenever a new product card version introduces its own
 * custom element in its own file (e.g. a future product-card-v3.js).
 */
(() => {
  if (window.productCardComponentsLoaded || !document.currentScript) {
    return;
  }

  window.productCardComponentsLoaded = true;

  const activeVersion = window.theme && window.theme.productCardVersion;
  const scripts = (window.theme && window.theme.productCardVersionScripts) || {};
  const scriptUrl = scripts[activeVersion];

  if (!scriptUrl) {
    return;
  }

  const script = document.createElement('script');
  script.src = scriptUrl;
  // `.defer` has no effect on dynamically created scripts — they default to
  // `async`. Forcing `async = false` makes it execute in insertion order,
  // after `splide.min.js` (which product-card-v2.js depends on), instead of
  // racing ahead as soon as it finishes downloading.
  script.async = false;
  document.head.appendChild(script);
})();

/**
 * Base Shopify/Dawn `product-card` custom element: handles swatch/variant
 * selection, add-to-cart and the quickview modal. Shared by product card v1 and v2.
 */
(() => {
  if (customElements.get('product-card')) {
    return;
  }

  class ProductCard extends HTMLElement {
    constructor() {
      super();

      this.variantsSelect = this.querySelector('[data-product-select]');
      this.swatches = this.querySelectorAll('[data-swatches] input');
      this.media = this.querySelector('[data-product-media]');
      this.form = this.querySelector('form');
      this.cartDrawer = document.querySelector('cart-drawer');
      this.quickviewTemplate = document.querySelector('#product-quickview');
      this.productTitle = this.dataset.productTitle;
      this.productId = this.dataset.productId;

      this.swatches.forEach((swatch) => {
        swatch.addEventListener('change', this.onSwatchChange.bind(this));
      })

      if (this.variantsSelect) {
        this.variantsSelect.addEventListener('change', this.onVariantChange.bind(this));
      }

      this.form && this.form.addEventListener('submit', this.onSubmit.bind(this));
    }

    onSwatchChange(event) {
      const optionSelector = `option[data-option${
        event.target.dataset.optionPosition
      }="${event.target.value}"]`;

      this.variantsSelect.value = this.variantsSelect.querySelector(optionSelector).value;
      this.variantsSelect.dispatchEvent(new Event('change'));
    }

    onVariantChange(event) {
      const selectedOption = event.target.selectedOptions[0];

      this.querySelectorAll('[data-product-link]').forEach((link) => {
        link.setAttribute('href', `${this.dataset.url}${selectedOption.value}`)
      })

      if (!selectedOption.dataset.imageSrc || !selectedOption.dataset.imageSrcset) {
        return;
      }

      this.media.classList.remove('has-hover');
      this.media.querySelector('[data-secondary-image]').style.display = 'none';
      this.media.querySelector('[data-primary-image]').src = selectedOption.dataset.imageSrc;
      this.media.querySelector('[data-primary-image]').srcset = selectedOption.dataset.imageSrcset;
    }

    onSubmit(event) {
      event.preventDefault();

      const submitter = event.submitter || event.target.querySelector('[type="submit"]');
      const dispatchSubmit = submitter.hasAttribute('data-url');

      if (!dispatchSubmit) {
        this.addItemToCart(submitter);

        return;
      }

      if (window.modals && window.modals[`Product-Quickview-${this.productId}`]) {
        const quickview = window.modals[`Product-Quickview-${this.productId}`];

        this.attachQuickview(submitter, quickview);

        return;
      }

      fetch(`${submitter.dataset.url}&variant=${this.variantsSelect.value}`)
        .then((response) => response.text())
        .then((responseText) => {
          this.buildQuickView(submitter, responseText);
        })
    }

    buildQuickView(submitter, responseText) {
      const quickview = this.quickviewTemplate.content.cloneNode(true).querySelector('.product-quickview');

      quickview.id = `${quickview.id}${this.productId}`;
      quickview.querySelectorAll('[data-modal-close]').forEach((element) => {
        element.id = `${element.id}${this.productId}`;
      });

      if (quickview.querySelector('[role="dialog"]').ariaLabel) {
        quickview.querySelector('[role="dialog"]').ariaLabel =
          quickview.querySelector('[role="dialog"]').ariaLabel.replace('{{ product_title }}', this.productTitle);
      }

      this.fillQuickview(quickview, responseText);
      this.attachQuickview(submitter, quickview);
    }

    fillQuickview(quickview, responseText) {
      const productDetails = new DOMParser()
        .parseFromString(responseText, 'text/html')
        .querySelector('[data-product-details]');
      const productFeaturedMedia = Array.from(
        new DOMParser()
        .parseFromString(responseText, 'text/html')
        .querySelectorAll('[data-product-media]')
      ).filter((media) => media.children[0].tagName.toLowerCase() !== 'modal-opener' && media.children[0].tagName.toLowerCase() !== 'deferred-media');

      productFeaturedMedia.forEach((media, index) => {
        media.children[0].setAttribute('data-product-quickview-media', '');

        if (index === 0) {
          return;
        }

        media.children[0].style.display = 'none';
      });
      quickview.querySelector('[data-media-wrapper]').innerHTML = Array.from(productFeaturedMedia).map((media) => media.innerHTML).join('');
      quickview.querySelector('[data-details-wrapper]').innerHTML = productDetails.innerHTML;

      this.bindModelProductLinks(quickview);
    }

    // The color/material "swatches" on the product page aren't Shopify variants —
    // they're links to sibling products (see product-form.liquid's
    // `main-product__model-product`). On the real product page that's a normal
    // navigation, but that same markup also gets cloned into the quickview, where
    // clicking one should swap the modal's content instead of leaving the page
    // (unless settings.quickview_model_products_open_page opts back into navigating).
    bindModelProductLinks(quickview) {
      if (window.theme && window.theme.quickviewModelProductsOpenPage) {
        return;
      }

      quickview.querySelectorAll('.main-product__model-product').forEach((link) => {
        link.addEventListener('click', (event) => {
          event.preventDefault();

          const separator = link.href.includes('?') ? '&' : '?';

          fetch(`${link.href}${separator}section_id=main-product`)
            .then((response) => response.text())
            .then((responseText) => {
              this.fillQuickview(quickview, responseText);
            });
        });
      });
    }

    attachQuickview(submitter, quickview) {
      document.body.append(quickview);
      setTimeout(() => {
        quickview.show(submitter);
      }, 1);
    }

    addItemToCart(submitter) {
      if (!this.cartDrawer) {
        this.form && this.form.submit()

        return;
      }

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData(this.form);
      formData.append('sections', this.cartDrawer.getSectionsToRender().map((section) => section.id));
      formData.append('sections_url', window.location.pathname);
      config.body = formData;

      this.cartDrawer.setActiveElement(document.activeElement);
      submitter && submitter.classList.add('is-loading');

      fetch(`${routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            window.console.error(response.status);

            return;
          }

          this.cartDrawer.handleAddedItem(response);
        })
        .catch((error) => {
          window.console.error(error);
        })
        .finally(() => {
          submitter && submitter.classList.remove('is-loading');
        });
    }
  }

  customElements.define('product-card', ProductCard);
})();
